import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { 
  LayoutDashboard, Calendar, Users, MessageSquare, Ticket, 
  Settings, Plus, Minus, Edit, Trash2, Send, AlertCircle,
  TrendingUp, UserCheck, CalendarDays, DollarSign, ChevronRight,
  Mail, Download, Search, Lock, User, Save, Smartphone, Star,
  Image, GripVertical, X, Upload, ChevronUp, ChevronDown, Volume2
} from "lucide-react";
import { checkAdminStatsChanges, notifyAdminChanges } from "@/services/adminNotificationService";
import { playNotificationSound } from "@/services/notificationService";

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, trend }) => (
  <div className="bg-obsidian-light border border-white/10 rounded-lg p-6" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-white/50 text-sm">{title}</p>
        <p className="text-3xl font-display text-white mt-1">{value}</p>
        {trend && (
          <p className="text-electric text-sm mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {trend}
          </p>
        )}
      </div>
      <div className="w-12 h-12 rounded-full bg-electric/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-electric" />
      </div>
    </div>
  </div>
);

// Overview Tab
const OverviewTab = ({ stats, events, token }) => {
  const upcomingEvents = events.slice(0, 5);
  const [installHistory, setInstallHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const hasCheckedStats = useRef(false);
  
  // Check for stat changes and play notification
  useEffect(() => {
    if (stats && !hasCheckedStats.current) {
      hasCheckedStats.current = true;
      const { changes, hasChanges } = checkAdminStatsChanges(stats);
      if (hasChanges && soundEnabled) {
        notifyAdminChanges(changes, toast.success);
      }
    }
  }, [stats, soundEnabled]);
  
  useEffect(() => {
    const fetchInstallHistory = async () => {
      try {
        const response = await axios.get(`${API}/admin/install-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInstallHistory(response.data.history || []);
      } catch (error) {
        console.error("Failed to fetch install history:", error);
      }
    };
    if (token) fetchInstallHistory();
  }, [token]);
  
  // Poll for stats changes every 60 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newStats = response.data;
        const { changes, hasChanges } = checkAdminStatsChanges(newStats);
        if (hasChanges && soundEnabled) {
          notifyAdminChanges(changes, toast.success);
        }
      } catch (error) {
        console.error("Failed to poll stats:", error);
      }
    }, 60000); // Check every 60 seconds
    
    return () => clearInterval(pollInterval);
  }, [token, soundEnabled]);
  
  // Calculate max for chart scaling
  const maxInstalls = Math.max(...installHistory.map(d => d.installs), 1);
  
  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    if (newState) {
      playNotificationSound();
      toast.success("Admin notifications enabled");
    } else {
      toast.info("Admin notifications muted");
    }
  };
  
  return (
    <div className="space-y-8">
      {/* Notification Toggle */}
      <div className="flex items-center justify-between bg-obsidian-light border border-white/10 rounded-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <Volume2 className={`w-5 h-5 ${soundEnabled ? 'text-electric' : 'text-white/30'}`} />
          <div>
            <p className="text-white text-sm font-medium">Admin Notifications</p>
            <p className="text-white/50 text-xs">Sound alerts for new subscribers & app installs</p>
          </div>
        </div>
        <Switch 
          checked={soundEnabled}
          onCheckedChange={toggleSound}
        />
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <StatCard title="Total Events" value={stats.total_events} icon={CalendarDays} />
        <StatCard title="Total Users" value={stats.total_users} icon={Users} />
        <StatCard title="Subscribers" value={stats.total_subscribers} icon={UserCheck} />
        <StatCard title="Tickets Sold" value={stats.total_tickets_sold} icon={Ticket} />
        <StatCard title="App Installs" value={stats.app_installs} icon={Smartphone} />
      </div>
      
      {/* App Installs Chart */}
      {installHistory.length > 0 && (
        <div className="bg-obsidian-light border border-white/10 rounded-lg p-6">
          <h3 className="font-display text-xl text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-electric" />
            App Installs (Last 30 Days)
          </h3>
          <div className="flex items-end gap-1 h-32" data-testid="install-chart">
            {installHistory.map((day, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                <div 
                  className="w-full bg-electric/80 rounded-t transition-all hover:bg-electric"
                  style={{ height: `${(day.installs / maxInstalls) * 100}%`, minHeight: day.installs > 0 ? '4px' : '0' }}
                  title={`${day.date}: ${day.installs} installs`}
                />
                <span className="text-[9px] text-white/30 hidden group-hover:block">
                  {day.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-white/40 text-xs">
            <span>{installHistory[0]?.date}</span>
            <span>{installHistory[installHistory.length - 1]?.date}</span>
          </div>
        </div>
      )}
      
      {/* Upcoming Events */}
      <div className="bg-obsidian-light border border-white/10 rounded-lg p-6">
        <h3 className="font-display text-xl text-white mb-4">Upcoming Events</h3>
        <div className="space-y-3">
          {upcomingEvents.map(event => (
            <Link 
              key={event.id} 
              to={`/admin/events`}
              className="flex items-center justify-between p-3 rounded-lg bg-obsidian hover:bg-white/5 transition-colors"
            >
              <div>
                <p className="text-white font-medium">{event.title}</p>
                <p className="text-white/50 text-sm">{event.date} at {event.time}</p>
              </div>
              <div className="text-right">
                <p className="text-electric">{event.tickets_sold || 0}/{event.capacity}</p>
                <p className="text-white/50 text-sm">tickets</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Events Management Tab
const EventsTab = ({ events, onRefresh, token }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "8:00 PM",
    performer: "",
    performer_bio: "",
    image_url: "",
    ticket_price: 10,
    capacity: 80,
    is_featured: false,
    sort_order: 0
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      date: "",
      time: "8:00 PM",
      performer: "",
      performer_bio: "",
      image_url: "",
      ticket_price: 10,
      capacity: 80,
      is_featured: false,
      sort_order: 0
    });
    setEditingEvent(null);
    setShowForm(false);
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title || "",
      description: event.description || "",
      date: event.date || "",
      time: event.time || "8:00 PM",
      performer: event.performer || "",
      performer_bio: event.performer_bio || "",
      image_url: event.image_url || "",
      ticket_price: event.ticket_price || 10,
      capacity: event.capacity || 80,
      is_featured: event.is_featured || false,
      sort_order: event.sort_order || 0
    });
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload/event-image`, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingEvent) {
        await axios.put(`${API}/admin/events/${editingEvent.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event updated!");
      } else {
        await axios.post(`${API}/admin/events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Event created!");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this event? This will also delete all RSVPs and posts.")) return;
    
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event deleted!");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleToggleFeatured = async (event) => {
    try {
      await axios.put(`${API}/admin/events/${event.id}`, {
        is_featured: !event.is_featured
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(event.is_featured ? "Removed from featured" : "Added to featured!");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update event");
    }
  };

  const handleMoveOrder = async (event, direction) => {
    const newOrder = (event.sort_order || 0) + (direction === 'up' ? -1 : 1);
    try {
      await axios.put(`${API}/admin/events/${event.id}`, {
        sort_order: newOrder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      toast.error("Failed to reorder");
    }
  };

  // Sort events by sort_order for display
  const sortedEvents = [...events].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h3 className="font-display text-xl text-white">Event Management</h3>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-electric text-obsidian hover:bg-electric-light gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Event
        </Button>
      </div>

      {/* Event Form */}
      {showForm && (
        <div className="bg-obsidian-light border border-white/10 rounded-lg p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-white font-display text-lg">
              {editingEvent ? "Edit Event" : "New Event"}
            </h4>
            <Button variant="ghost" size="icon" onClick={resetForm} className="text-white/50">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Event Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="Monday Nights with..."
                  required
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Performer *</label>
                <Input
                  value={formData.performer}
                  onChange={(e) => setFormData({ ...formData, performer: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="Artist name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Event description..."
                rows={3}
                required
              />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Date *</label>
                <Input
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="2026-03-14 or Every Monday"
                  required
                />
                <p className="text-white/40 text-xs mt-1">Use "Every Monday" for recurring</p>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Time *</label>
                <Input
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="8:00 PM"
                  required
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Ticket Price ($)</label>
                <Input
                  type="number"
                  value={formData.ticket_price}
                  onChange={(e) => setFormData({ ...formData, ticket_price: parseFloat(e.target.value) || 0 })}
                  className="bg-obsidian border-white/10 text-white"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Capacity</label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 80 })}
                  className="bg-obsidian border-white/10 text-white"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Sort Order</label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="bg-obsidian border-white/10 text-white"
                />
                <p className="text-white/40 text-xs mt-1">Lower numbers appear first</p>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-white/70 text-sm mb-2">Event Image</label>
              <div className="flex flex-col sm:flex-row gap-4">
                {formData.image_url && (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-obsidian">
                    <img 
                      src={formData.image_url.startsWith('/') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url} 
                      alt="Event" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: "" })}
                      className="absolute top-1 right-1 bg-red-500 rounded-full p-1"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                )}
                <div className="flex-1">
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-electric/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? (
                      <div className="w-5 h-5 border-2 border-electric border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5 text-white/50" />
                    )}
                    <span className="text-white/50 text-sm">
                      {uploading ? "Uploading..." : "Upload image"}
                    </span>
                  </label>
                  <p className="text-white/40 text-xs mt-2">Or paste image URL:</p>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="bg-obsidian border-white/10 text-white mt-1"
                    placeholder="/assets/event-image.jpg"
                  />
                </div>
              </div>
            </div>

            {/* Featured Toggle */}
            <div className="flex items-center justify-between p-4 bg-obsidian rounded-lg">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-white font-medium">Featured Event</p>
                  <p className="text-white/50 text-sm">Show prominently on home page</p>
                </div>
              </div>
              <Switch 
                checked={formData.is_featured}
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="bg-electric text-obsidian hover:bg-electric-light gap-2">
                <Save className="w-4 h-4" />
                {editingEvent ? "Update Event" : "Create Event"}
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm} className="text-white/50">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      <div className="space-y-3">
        {sortedEvents.map((event, index) => (
          <div 
            key={event.id}
            className={`bg-obsidian-light border rounded-lg p-4 ${event.is_featured ? 'border-amber-500/50' : 'border-white/10'}`}
            data-testid={`event-card-${event.id}`}
          >
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Event Image */}
              {event.image_url && (
                <div className="w-full lg:w-24 h-32 lg:h-24 rounded-lg overflow-hidden bg-obsidian flex-shrink-0">
                  <img 
                    src={event.image_url.startsWith('/') ? `${API.replace('/api', '')}${event.image_url}` : event.image_url}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {/* Event Details */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h4 className="text-white font-display text-lg">{event.title}</h4>
                  {event.is_featured && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded flex items-center gap-1">
                      <Star className="w-3 h-3" /> Featured
                    </span>
                  )}
                </div>
                <p className="text-white/50 text-sm">{event.performer} • {event.date} at {event.time}</p>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                  <span className="text-electric">${event.ticket_price}</span>
                  <span className="text-white/50">{event.tickets_sold || 0}/{event.capacity} tickets</span>
                  <span className="text-white/50">{event.rsvp_count || 0} RSVPs</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-row lg:flex-col items-center gap-2 flex-shrink-0">
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleMoveOrder(event, 'up')}
                    className="text-white/50 hover:text-white h-8 w-8"
                    disabled={index === 0}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleMoveOrder(event, 'down')}
                    className="text-white/50 hover:text-white h-8 w-8"
                    disabled={index === sortedEvents.length - 1}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleToggleFeatured(event)}
                  className={`h-8 w-8 ${event.is_featured ? 'text-amber-400' : 'text-white/30 hover:text-amber-400'}`}
                  title={event.is_featured ? "Remove from featured" : "Add to featured"}
                >
                  <Star className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(event)}
                  className="text-white/50 hover:text-electric h-8 w-8"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(event.id)}
                  className="text-white/30 hover:text-red-400 h-8 w-8"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}

        {events.length === 0 && (
          <div className="text-center py-12 text-white/40 border border-white/10 rounded-lg">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No events yet</p>
            <Button onClick={() => setShowForm(true)} className="mt-4 bg-electric text-obsidian">
              Create Your First Event
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Team Management Tab
const TeamTab = ({ token, user }) => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ email: "", name: "", role: "staff" });
  
  const isAdmin = user?.role === "admin";
  
  useEffect(() => {
    fetchTeam();
  }, []);
  
  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/admin/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeam(response.data);
    } catch (error) {
      console.error("Failed to fetch team:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/team/invite`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      if (response.data.temp_password) {
        toast.info(`Temporary password: ${response.data.temp_password}`, { duration: 10000 });
      }
      setShowInvite(false);
      setInviteData({ email: "", name: "", role: "staff" });
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite team member");
    }
  };
  
  const handleRemove = async (userId) => {
    if (!window.confirm("Remove this team member's access?")) return;
    try {
      await axios.delete(`${API}/admin/team/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member removed");
      fetchTeam();
    } catch (error) {
      toast.error("Failed to remove team member");
    }
  };
  
  if (loading) {
    return <div className="text-white/50">Loading team...</div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl text-white">Team Members</h3>
        {isAdmin && (
          <Button
            onClick={() => setShowInvite(!showInvite)}
            className="bg-electric text-obsidian hover:bg-electric-light"
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>
      
      {showInvite && (
        <form onSubmit={handleInvite} className="bg-obsidian-light border border-white/10 rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="Email"
              type="email"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              className="bg-obsidian border-white/20 text-white"
              required
            />
            <Input
              placeholder="Name"
              value={inviteData.name}
              onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
              className="bg-obsidian border-white/20 text-white"
              required
            />
            <select
              value={inviteData.role}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              className="bg-obsidian border border-white/20 text-white rounded-md px-3"
            >
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="bg-electric text-obsidian">
              Send Invite
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowInvite(false)} className="text-white/50">
              Cancel
            </Button>
          </div>
        </form>
      )}
      
      <div className="space-y-3">
        {team.map(member => (
          <div 
            key={member.id}
            className="bg-obsidian-light border border-white/10 rounded-lg p-4 flex items-center justify-between"
          >
            <div>
              <p className="text-white font-medium">{member.name}</p>
              <p className="text-white/50 text-sm">{member.email}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 rounded text-sm ${
                member.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                member.role === 'manager' ? 'bg-blue-500/20 text-blue-400' :
                'bg-green-500/20 text-green-400'
              }`}>
                {member.role}
              </span>
              {isAdmin && member.id !== user.id && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(member.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Team Messages Tab
const MessagesTab = ({ token, user }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);
  
  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      await axios.post(`${API}/admin/messages`, {
        content: newMessage,
        priority
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage("");
      setPriority("normal");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };
  
  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };
  
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString();
  };
  
  return (
    <div className="space-y-6">
      <h3 className="font-display text-xl text-white">Team Communication</h3>
      
      {/* New Message Form */}
      <form onSubmit={handleSend} className="bg-obsidian-light border border-white/10 rounded-lg p-4">
        <Textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Write a message to the team..."
          className="bg-obsidian border-white/20 text-white mb-3"
          rows={3}
        />
        <div className="flex items-center justify-between">
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="bg-obsidian border border-white/20 text-white rounded-md px-3 py-2"
          >
            <option value="normal">Normal</option>
            <option value="important">Important</option>
            <option value="urgent">Urgent</option>
          </select>
          <Button type="submit" className="bg-electric text-obsidian hover:bg-electric-light">
            <Send className="w-4 h-4 mr-2" />
            Send
          </Button>
        </div>
      </form>
      
      {/* Messages List */}
      <div className="space-y-3">
        {messages.map(message => (
          <div 
            key={message.id}
            className={`bg-obsidian-light border rounded-lg p-4 ${
              message.priority === 'urgent' ? 'border-red-500/50' :
              message.priority === 'important' ? 'border-amber-500/50' :
              'border-white/10'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium">{message.user_name}</span>
                {message.priority !== 'normal' && (
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    message.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {message.priority}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/40 text-sm">{formatTime(message.created_at)}</span>
                {(user?.role === 'admin' || message.user_id === user?.id) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(message.id)}
                    className="text-white/30 hover:text-red-400 h-6 w-6"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            <p className="text-white/80 mt-2 whitespace-pre-wrap">{message.content}</p>
          </div>
        ))}
        
        {messages.length === 0 && !loading && (
          <div className="text-center py-12 text-white/40">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Subscribers Tab
const SubscribersTab = ({ token }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifySubject, setNotifySubject] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [sending, setSending] = useState(false);
  
  useEffect(() => {
    fetchSubscribers();
  }, []);
  
  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error("Failed to fetch subscribers:", error);
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/subscribers/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `brownstone_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Subscribers exported successfully!");
    } catch (error) {
      toast.error("Failed to export subscribers");
    } finally {
      setExporting(false);
    }
  };
  
  const handleNotifyAll = async () => {
    if (!notifySubject.trim() || !notifyMessage.trim()) {
      toast.error("Please enter both subject and message");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/notify-subscribers`, 
        { subject: notifySubject, message: notifyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowNotifyModal(false);
      setNotifySubject("");
      setNotifyMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };
  
  const handleDelete = async (subscriberId, email) => {
    if (!window.confirm(`Remove ${email} from the mailing list?`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${subscriberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscriber removed");
      fetchSubscribers();
    } catch (error) {
      toast.error("Failed to remove subscriber");
    }
  };
  
  const filteredSubscribers = subscribers.filter(sub => 
    sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sub.name && sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading) {
    return <div className="text-white/50">Loading subscribers...</div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-xl text-white">Mailing List Subscribers</h3>
          <p className="text-white/50 text-sm mt-1">{subscribers.length} total subscribers</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowNotifyModal(true)}
            disabled={subscribers.length === 0}
            className="bg-rose-500 hover:bg-rose-600 text-white"
            data-testid="notify-subscribers-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Notify All
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || subscribers.length === 0}
            className="bg-electric text-obsidian hover:bg-electric-light"
            data-testid="export-subscribers-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
      
      {/* Notify Modal */}
      {showNotifyModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={() => setShowNotifyModal(false)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95%] max-w-lg bg-obsidian-light border border-white/10 rounded-2xl p-6 shadow-2xl">
            <h3 className="font-display text-xl text-white mb-4">Send Notification to Subscribers</h3>
            <p className="text-white/50 text-sm mb-4">
              This will send an email to all {subscribers.length} subscribers.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-white/70 text-sm block mb-1">Subject</label>
                <Input 
                  value={notifySubject}
                  onChange={(e) => setNotifySubject(e.target.value)}
                  placeholder="e.g., Exciting News from The Brownstone!"
                  className="bg-obsidian border-white/20 text-white"
                />
              </div>
              <div>
                <label className="text-white/70 text-sm block mb-1">Message</label>
                <Textarea 
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  placeholder="Write your message here..."
                  className="bg-obsidian border-white/20 text-white min-h-[150px]"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowNotifyModal(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNotifyAll}
                disabled={sending || !notifySubject.trim() || !notifyMessage.trim()}
                className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
              >
                {sending ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to All
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      )}
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <Input
          placeholder="Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="bg-obsidian-light border-white/20 text-white pl-10"
          data-testid="search-subscribers"
        />
      </div>
      
      {/* Subscribers List */}
      <div className="bg-obsidian-light border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-sm font-medium px-4 py-3">Email</th>
                <th className="text-left text-white/50 text-sm font-medium px-4 py-3">Name</th>
                <th className="text-left text-white/50 text-sm font-medium px-4 py-3">Subscribed</th>
                <th className="text-right text-white/50 text-sm font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscribers.map(subscriber => (
                <tr 
                  key={subscriber.id} 
                  className="border-b border-white/5 hover:bg-white/5"
                  data-testid={`subscriber-row-${subscriber.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-electric" />
                      <span className="text-white">{subscriber.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/70">
                    {subscriber.name || <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-4 py-3 text-white/50 text-sm">
                    {formatDate(subscriber.subscribed_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(subscriber.id, subscriber.email)}
                      className="text-white/30 hover:text-red-400 h-8 w-8"
                      data-testid={`delete-subscriber-${subscriber.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredSubscribers.length === 0 && (
          <div className="text-center py-12 text-white/40">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{searchTerm ? "No subscribers match your search" : "No subscribers yet"}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Account Settings Tab
const AccountSettingsTab = ({ token, user, refreshUser }) => {
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || ""
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      await axios.put(`${API}/account/profile`, {
        name: profileData.name,
        email: profileData.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Profile updated successfully!");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSavingPassword(true);
    
    try {
      await axios.put(`${API}/account/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Password updated successfully!");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Settings */}
      <div className="bg-obsidian-light border border-white/10 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-electric/10 flex items-center justify-center">
            <User className="w-5 h-5 text-electric" />
          </div>
          <div>
            <h2 className="font-display text-xl text-white">Profile Settings</h2>
            <p className="text-white/50 text-sm">Update your account information</p>
          </div>
        </div>
        
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Full Name</label>
            <Input
              type="text"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              className="bg-obsidian border-white/10 text-white"
              placeholder="Your name"
              data-testid="settings-name"
            />
          </div>
          
          <div>
            <label className="block text-white/70 text-sm mb-2">Email Address</label>
            <Input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              className="bg-obsidian border-white/10 text-white"
              placeholder="your@email.com"
              data-testid="settings-email"
            />
          </div>
          
          <div className="flex items-center gap-4 pt-2">
            <Button
              type="submit"
              disabled={savingProfile}
              className="bg-electric text-obsidian hover:bg-electric-light gap-2"
              data-testid="save-profile-btn"
            >
              {savingProfile ? (
                <div className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Profile
            </Button>
            <span className="text-white/40 text-sm">Role: {user?.role?.toUpperCase()}</span>
          </div>
        </form>
      </div>

      {/* Password Settings */}
      <div className="bg-obsidian-light border border-white/10 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
            <Lock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="font-display text-xl text-white">Change Password</h2>
            <p className="text-white/50 text-sm">Update your account password</p>
          </div>
        </div>
        
        <form onSubmit={handlePasswordUpdate} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Current Password</label>
            <Input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              className="bg-obsidian border-white/10 text-white"
              placeholder="Enter current password"
              required
              data-testid="settings-current-password"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">New Password</label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Enter new password"
                required
                minLength={6}
                data-testid="settings-new-password"
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Confirm New Password</label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Confirm new password"
                required
                minLength={6}
                data-testid="settings-confirm-password"
              />
            </div>
          </div>
          
          <Button
            type="submit"
            disabled={savingPassword}
            className="bg-amber-500 text-obsidian hover:bg-amber-400 gap-2"
            data-testid="save-password-btn"
          >
            {savingPassword ? (
              <div className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            Update Password
          </Button>
        </form>
      </div>

      {/* Account Info */}
      <div className="bg-obsidian-light/50 border border-white/5 rounded-lg p-6">
        <h3 className="text-white/70 text-sm font-medium mb-3">Account Information</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-white/50">User ID</span>
            <span className="text-white/70 font-mono text-xs">{user?.id?.substring(0, 12)}...</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Account Created</span>
            <span className="text-white/70">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Role</span>
            <span className="text-electric">{user?.role?.toUpperCase()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Tab
const CalendarTab = ({ events }) => {
  const [viewMonth, setViewMonth] = useState(new Date());
  
  // Filter events that have specific dates (not recurring)
  const datedEvents = events.filter(e => !e.date.startsWith("Every"));
  
  // Get days in month
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  
  const getEventsForDay = (day) => {
    const dateStr = `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return datedEvents.filter(e => e.date === dateStr);
  };
  
  const prevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1));
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl text-white">Event Calendar</h3>
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={prevMonth} className="text-white/50">
            &lt;
          </Button>
          <span className="text-white font-medium min-w-[150px] text-center">
            {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </span>
          <Button variant="ghost" onClick={nextMonth} className="text-white/50">
            &gt;
          </Button>
        </div>
      </div>
      
      {/* Recurring Events */}
      <div className="bg-obsidian-light border border-white/10 rounded-lg p-4">
        <h4 className="text-white/70 text-sm mb-3">Weekly Residencies</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {events.filter(e => e.date.startsWith("Every")).map(event => (
            <div key={event.id} className="bg-obsidian rounded-lg p-3">
              <p className="text-electric text-sm">{event.date}</p>
              <p className="text-white font-medium text-sm">{event.title}</p>
              <p className="text-white/40 text-xs">{event.tickets_sold || 0}/{event.capacity} tickets</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="bg-obsidian-light border border-white/10 rounded-lg p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div key={day} className="text-center text-white/40 text-sm py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before month starts */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          
          {/* Days of the month */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayEvents = getEventsForDay(day);
            const isToday = new Date().toDateString() === new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day).toDateString();
            
            return (
              <div 
                key={day}
                className={`aspect-square p-1 rounded-lg border ${
                  isToday ? 'border-electric bg-electric/10' : 'border-transparent hover:border-white/20'
                } ${dayEvents.length > 0 ? 'bg-electric/5' : ''}`}
              >
                <div className="text-right">
                  <span className={`text-sm ${isToday ? 'text-electric' : 'text-white/70'}`}>{day}</span>
                </div>
                {dayEvents.map(event => (
                  <div 
                    key={event.id}
                    className="text-xs bg-electric/20 text-electric rounded px-1 py-0.5 truncate mt-1"
                    title={event.title}
                  >
                    {event.title.substring(0, 10)}...
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_events: 0,
    total_users: 0,
    total_subscribers: 0,
    total_rsvps: 0,
    upcoming_events: 0,
    total_tickets_sold: 0,
    app_installs: 0
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const currentPath = location.pathname.replace("/admin", "") || "/";
  
  const navItems = [
    { path: "/", label: "Overview", icon: LayoutDashboard },
    { path: "/events", label: "Events", icon: Ticket },
    { path: "/calendar", label: "Calendar", icon: Calendar },
    { path: "/subscribers", label: "Subscribers", icon: Mail },
    { path: "/team", label: "Team", icon: Users },
    { path: "/messages", label: "Messages", icon: MessageSquare },
    { path: "/settings", label: "Settings", icon: Settings },
  ];
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/events`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have admin access");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-obsidian pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <h1 className="font-display text-2xl sm:text-3xl text-white">Admin Dashboard</h1>
          <p className="text-white/50 mt-1 text-sm sm:text-base">Welcome back, {user?.name}</p>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-8">
          {/* Mobile Navigation - Horizontal Scrollable */}
          <nav className="lg:hidden overflow-x-auto -mx-4 px-4">
            <div className="bg-obsidian-light border border-white/10 rounded-lg p-1 inline-flex gap-1 min-w-max">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={`/admin${item.path === "/" ? "" : item.path}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors whitespace-nowrap text-sm ${
                    currentPath === item.path 
                      ? "bg-electric/10 text-electric" 
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </nav>
          
          {/* Desktop Sidebar Navigation */}
          <nav className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="bg-obsidian-light border border-white/10 rounded-lg p-2 lg:sticky lg:top-24">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={`/admin${item.path === "/" ? "" : item.path}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    currentPath === item.path 
                      ? "bg-electric/10 text-electric" 
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {currentPath === item.path && (
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  )}
                </Link>
              ))}
            </div>
          </nav>
          
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Routes>
              <Route path="/" element={<OverviewTab stats={stats} events={events} token={token} />} />
              <Route path="/events" element={<EventsTab events={events} onRefresh={fetchData} token={token} />} />
              <Route path="/calendar" element={<CalendarTab events={events} />} />
              <Route path="/subscribers" element={<SubscribersTab token={token} />} />
              <Route path="/team" element={<TeamTab token={token} user={user} />} />
              <Route path="/messages" element={<MessagesTab token={token} user={user} />} />
              <Route path="/settings" element={<AccountSettingsTab token={token} user={user} />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
